import { buildWorkbook, downloadBlob, header, hkd } from './xlsx.js';
import { dateLabel, monthLabel, nameOf, timestampLabel, unitName, auditLabel } from './format.js';

/**
 * Excel and PDF are generated in the browser from ledger data the Worker has
 * already authorized and returned. Reports contain stable 證明參考 values that link
 * back into the authorized App; they never embed an image, bundle images or list
 * an original image file name.
 */
const CATEGORY_DEPOSIT = '按金';

function memberName(ledger, memberId) {
  return nameOf(ledger.members, memberId);
}

function unitLabel(ledger, unitId) {
  return unitName(ledger.units, unitId);
}

function itemStatusLabel(item) {
  if (item.status === 'cancelled') return '已取消';
  if (item.historical) return item.historical_outcome === 'settled' ? '歷史補錄 · 已結清' : '歷史補錄 · 已取消';
  return '生效中';
}

function filterSummary(ledger) {
  const filters = ledger.filters;
  return [
    `期間 ${monthLabel(filters.from_month)} 至 ${monthLabel(filters.to_month)}`,
    `單位 ${filters.unit_id ? unitLabel(ledger, filters.unit_id) : '全部'}`,
    `分類 ${filters.category || '全部'}`,
    `成員 ${filters.member_id ? memberName(ledger, filters.member_id) : '全部'}`,
  ];
}

export function ledgerFileName(ledger, extension) {
  const filters = ledger.filters;
  return `夾租簿帳簿_${filters.from_month}_${filters.to_month}${filters.unit_id ? `_${unitLabel(ledger, filters.unit_id)}` : ''}.${extension}`;
}

export function buildLedgerWorkbook(ledger, actorName) {
  const sheets = [];

  sheets.push({
    name: '摘要',
    columnWidths: [26, 30, 18, 18],
    rows: [
      [{ value: '夾租簿 · 合租帳務帳簿', style: 1 }],
      ['匯出者', actorName],
      ['匯出時間 (UTC)', new Date().toISOString()],
      ...filterSummary(ledger).map((line) => ['篩選條件', line]),
      [],
      header(['合計項目', '金額 (HKD)', '金額 (港仙)', '說明']),
      ['支出（按帳務月份，不含按金）', hkd(ledger.overview.expenses_cents), ledger.overview.expenses_cents, '生效中付款項目總額'],
      ['收入（結餘已退回）', hkd(ledger.overview.income_cents), ledger.overview.income_cents, '已退回結餘'],
      ['淨額', hkd(ledger.overview.net_cents), ledger.overview.net_cents, '收入減支出'],
      ['按金權益', hkd(ledger.overview.deposit_equity_cents), ledger.overview.deposit_equity_cents, '獨立於一般消費'],
      ['群組合計（所有項目總額）', hkd(ledger.overview.group_total_cents), ledger.overview.group_total_cents, '包含已取消及歷史項目'],
      [],
      header(['成員', '應付款 (HKD)', '應付款 (港仙)', '已確認 (港仙)', '仍待付款 (港仙)', '待處理結餘 (港仙)', '按金權益 (港仙)']),
      ...ledger.overview.member_totals.map((total) => [
        memberName(ledger, total.member_id),
        hkd(total.allocated_cents),
        total.allocated_cents,
        total.confirmed_cents,
        total.remaining_cents,
        total.pending_balance_cents,
        total.deposit_equity_cents,
      ]),
    ],
  });

  sheets.push({
    name: '付款項目',
    columnWidths: [24, 10, 12, 10, 16, 16, 14, 14, 12, 18, 12, 12],
    rows: [
      header(['付款項目', '單位', '帳務月份', '分類', '總額 (HKD)', '總額 (港仙)', '收款人', '確認人', '到期日', '狀態', '來源', '建立者']),
      ...ledger.items.map((item) => [
        item.title,
        unitLabel(ledger, item.unit_id),
        item.accounting_month,
        item.category,
        hkd(item.total_cents),
        item.total_cents,
        item.recipient_kind === 'member' ? memberName(ledger, item.recipient_member_id) : item.recipient_name,
        item.historical ? '—' : memberName(ledger, item.confirmer_member_id),
        item.due_date,
        itemStatusLabel(item),
        item.origin === 'recurrence' ? '循環設定' : item.origin === 'historical' ? '歷史補錄' : '手動發出',
        memberName(ledger, item.creator_member_id),
      ]),
    ],
  });

  sheets.push({
    name: '成員分攤',
    columnWidths: [24, 10, 12, 12, 14, 14, 14, 14, 14, 20],
    rows: [
      header(['付款項目', '單位', '帳務月份', '成員', '應付款 (HKD)', '應付款 (港仙)', '已確認 (港仙)', '仍待付款 (港仙)', '待處理結餘 (港仙)', '狀態']),
      ...ledger.items.flatMap((item) => item.dues
        .filter((due) => due.amount_cents > 0 || due.confirmed_cents > 0)
        .map((due) => [
          item.title,
          unitLabel(ledger, item.unit_id),
          item.accounting_month,
          memberName(ledger, due.member_id),
          hkd(due.amount_cents),
          due.amount_cents,
          due.confirmed_cents,
          due.remaining_cents,
          due.pending_balance_cents,
          `${due.state_label}${due.overdue ? '（已逾期）' : ''}`,
        ])),
    ],
  });

  sheets.push({
    name: '付款紀錄',
    columnWidths: [16, 24, 10, 12, 12, 12, 14, 14, 16, 14],
    rows: [
      header(['證明參考', '付款項目', '單位', '分類', '帳務月份', '成員', '金額 (HKD)', '金額 (港仙)', '狀態', '日期']),
      ...ledger.payments.map((payment) => [
        payment.reference,
        payment.item_title,
        unitLabel(ledger, payment.unit_id),
        payment.category,
        payment.accounting_month,
        memberName(ledger, payment.member_id),
        hkd(payment.amount_cents),
        payment.amount_cents,
        payment.state_label,
        payment.date,
      ]),
    ],
  });

  sheets.push({
    name: '調整應付款',
    columnWidths: [24, 12, 16, 16, 30, 14, 22],
    rows: [
      header(['付款項目', '成員', '舊金額 (港仙)', '新金額 (港仙)', '原因', '操作者', '時間 (UTC)']),
      ...ledger.adjustments.map((adjustment) => [
        adjustment.item_title,
        memberName(ledger, adjustment.member_id),
        adjustment.old_amount_cents,
        adjustment.new_amount_cents,
        adjustment.reason,
        memberName(ledger, adjustment.actor_member_id),
        adjustment.created_at,
      ]),
    ],
  });

  sheets.push({
    name: '結餘',
    columnWidths: [24, 12, 14, 14, 14, 16, 14, 24],
    rows: [
      header(['付款項目', '成員', '結餘 (港仙)', '已處理 (港仙)', '待處理 (港仙)', '來源', '處理方式', '處理日期／備註']),
      ...ledger.balances.map((balance) => [
        balance.item_title,
        memberName(ledger, balance.member_id),
        balance.amount_cents,
        balance.resolved_cents,
        balance.pending_cents,
        balance.origin,
        balance.dispositions.map((disposition) => (disposition.kind === 'returned' ? '已退回' : '已轉用')).join('、') || '待處理',
        balance.dispositions.map((disposition) => `${disposition.handled_on}${disposition.reason ? ` ${disposition.reason}` : ''}`).join('；'),
      ]),
    ],
  });

  const depositRows = ledger.items.filter((item) => item.category === CATEGORY_DEPOSIT);
  sheets.push({
    name: '按金權益',
    columnWidths: [24, 10, 12, 14, 14, 14],
    rows: [
      [{ value: '按金權益是成員日後應獲退回的份額，不視為一般消費。', style: 1 }],
      header(['付款項目', '單位', '成員', '應付款 (港仙)', '已確認 (港仙)', '狀態']),
      ...depositRows.flatMap((item) => item.dues.filter((due) => due.amount_cents > 0).map((due) => [
        item.title,
        unitLabel(ledger, item.unit_id),
        memberName(ledger, due.member_id),
        due.amount_cents,
        due.confirmed_cents,
        due.state_label,
      ])),
      [],
      header(['成員', '按金權益 (港仙)']),
      ...ledger.overview.member_totals.map((total) => [memberName(ledger, total.member_id), total.deposit_equity_cents]),
    ],
  });

  const historicalItems = ledger.items.filter((item) => item.historical);
  sheets.push({
    name: '歷史補錄',
    columnWidths: [24, 10, 12, 14, 14, 16, 16, 22, 24],
    rows: [
      header(['付款項目', '單位', '帳目日期', '總額 (港仙)', '結果', '成員', '歷史付款 (港仙)', '付款日期／證明參考', '補錄者／補錄時間']),
      ...historicalItems.flatMap((item) => item.dues.flatMap((due) => (
        due.historical_payments.length
          ? due.historical_payments.map((payment) => [
            item.title,
            unitLabel(ledger, item.unit_id),
            item.issue_date,
            item.total_cents,
            item.historical_outcome === 'settled' ? '已結清' : '已取消',
            memberName(ledger, due.member_id),
            payment.amount_cents,
            `${payment.paid_on} / ${payment.reference}`,
            `${memberName(ledger, payment.recorded_by)} / ${payment.recorded_at}`,
          ])
          : [[
            item.title,
            unitLabel(ledger, item.unit_id),
            item.issue_date,
            item.total_cents,
            item.historical_outcome === 'settled' ? '已結清' : '已取消',
            memberName(ledger, due.member_id),
            0,
            '—',
            `${memberName(ledger, item.recorded_by)} / ${item.recorded_at}`,
          ]]
      ))),
    ],
  });

  const categoryTotals = new Map();
  for (const item of ledger.items.filter((candidate) => candidate.status !== 'cancelled')) {
    const key = item.category;
    categoryTotals.set(key, (categoryTotals.get(key) || 0) + item.total_cents);
  }
  const unitTotals = new Map();
  for (const item of ledger.items.filter((candidate) => candidate.status !== 'cancelled')) {
    unitTotals.set(item.unit_id, (unitTotals.get(item.unit_id) || 0) + item.total_cents);
  }
  sheets.push({
    name: '分類總數',
    columnWidths: [16, 16, 16],
    rows: [
      header(['分類', '總額 (HKD)', '總額 (港仙)']),
      ...[...categoryTotals.entries()].map(([category, cents]) => [category, hkd(cents), cents]),
      [],
      header(['單位', '總額 (HKD)', '總額 (港仙)']),
      ...[...unitTotals.entries()].map(([unitId, cents]) => [unitLabel(ledger, unitId), hkd(cents), cents]),
    ],
  });

  sheets.push({
    name: '留痕',
    columnWidths: [22, 22, 14, 26, 30, 40],
    rows: [
      header(['時間 (UTC)', '操作', '操作者', '對象', '原因', '前後內容']),
      ...ledger.audit_events.map((event) => [
        event.created_at,
        auditLabel(event.action),
        memberName(ledger, event.actor_member_id),
        `${event.entity_type} ${event.entity_id || ''}`.trim(),
        event.reason || '',
        `${event.before_json ? `前:${event.before_json} ` : ''}${event.after_json ? `後:${event.after_json}` : ''}`.slice(0, 1000),
      ]),
    ],
  });

  return buildWorkbook(sheets);
}

let fontCache = null;

async function loadCjkFont() {
  if (fontCache) return fontCache;
  const response = await fetch('/fonts/NotoSansTC-Regular.ttf');
  if (!response.ok) throw new Error('無法載入 PDF 中文字型');
  fontCache = new Uint8Array(await response.arrayBuffer());
  return fontCache;
}

/** PDF 統計表: a stable summary for the selected period, unit, category and member. */
export async function buildLedgerPdf(ledger, actorName) {
  const [{ PDFDocument, StandardFonts, rgb }, fontkitModule, fontBytes] = await Promise.all([
    import('pdf-lib'),
    import('@pdf-lib/fontkit'),
    loadCjkFont(),
  ]);

  const document = await PDFDocument.create();
  document.registerFontkit(fontkitModule.default || fontkitModule);
  // Subset embedding keeps the report small even though the source font is a full
  // Traditional Chinese face.
  const cjk = await document.embedFont(fontBytes, { subset: true });
  const mono = await document.embedFont(StandardFonts.Helvetica);
  const monoBold = await document.embedFont(StandardFonts.HelveticaBold);

  const pageSize = [595.28, 841.89];
  const margin = 40;
  let page = document.addPage(pageSize);
  let cursor = pageSize[1] - margin;

  const newPage = () => {
    page = document.addPage(pageSize);
    cursor = pageSize[1] - margin;
  };
  const ensure = (needed) => {
    if (cursor - needed < margin) newPage();
  };
  const isAscii = (text) => /^[\x20-\x7E]*$/.test(text);
  const write = (text, { size = 10, x = margin, bold = false, colour = rgb(0.11, 0.11, 0.1) } = {}) => {
    const value = String(text ?? '');
    const font = isAscii(value) ? (bold ? monoBold : mono) : cjk;
    page.drawText(value, { x, y: cursor, size, font, color: colour });
  };
  const line = (text, options = {}) => {
    ensure(options.size ? options.size + 6 : 16);
    write(text, options);
    cursor -= (options.size || 10) + 6;
  };
  const table = (columns, rows) => {
    const draw = (values, bold) => {
      ensure(16);
      let x = margin;
      values.forEach((value, index) => {
        write(value, { x, size: 9, bold });
        x += columns[index].width;
      });
      cursor -= 15;
    };
    draw(columns.map((column) => column.label), true);
    page.drawLine({
      start: { x: margin, y: cursor + 10 },
      end: { x: pageSize[0] - margin, y: cursor + 10 },
      thickness: 0.5,
      color: rgb(0.8, 0.78, 0.75),
    });
    rows.forEach((row) => draw(row, false));
    cursor -= 8;
  };

  line('夾租簿 · 合租帳務統計表', { size: 18 });
  line(`匯出者 ${actorName} · 匯出時間 ${new Date().toISOString()} (UTC)`, { size: 9, colour: rgb(0.45, 0.43, 0.4) });
  filterSummary(ledger).forEach((text) => line(text, { size: 10 }));
  cursor -= 6;

  line('期間合計', { size: 13 });
  table(
    [{ label: '項目', width: 220 }, { label: 'HKD', width: 120 }, { label: '港仙', width: 120 }],
    [
      ['支出（不含按金）', (ledger.overview.expenses_cents / 100).toFixed(2), String(ledger.overview.expenses_cents)],
      ['收入（結餘已退回）', (ledger.overview.income_cents / 100).toFixed(2), String(ledger.overview.income_cents)],
      ['淨額', (ledger.overview.net_cents / 100).toFixed(2), String(ledger.overview.net_cents)],
      ['按金權益', (ledger.overview.deposit_equity_cents / 100).toFixed(2), String(ledger.overview.deposit_equity_cents)],
      ['群組合計', (ledger.overview.group_total_cents / 100).toFixed(2), String(ledger.overview.group_total_cents)],
    ],
  );

  line('成員合計', { size: 13 });
  table(
    [
      { label: '成員', width: 90 },
      { label: '應付款', width: 90 },
      { label: '已確認', width: 90 },
      { label: '仍待付款', width: 90 },
      { label: '待處理結餘', width: 90 },
      { label: '按金權益', width: 60 },
    ],
    ledger.overview.member_totals.map((total) => [
      memberName(ledger, total.member_id),
      (total.allocated_cents / 100).toFixed(2),
      (total.confirmed_cents / 100).toFixed(2),
      (total.remaining_cents / 100).toFixed(2),
      (total.pending_balance_cents / 100).toFixed(2),
      (total.deposit_equity_cents / 100).toFixed(2),
    ]),
  );

  const categoryTotals = new Map();
  for (const item of ledger.items.filter((candidate) => candidate.status !== 'cancelled')) {
    categoryTotals.set(item.category, (categoryTotals.get(item.category) || 0) + item.total_cents);
  }
  line('分類統計', { size: 13 });
  table(
    [{ label: '分類', width: 160 }, { label: 'HKD', width: 120 }, { label: '港仙', width: 120 }],
    [...categoryTotals.entries()].map(([category, cents]) => [
      category, (cents / 100).toFixed(2), String(cents),
    ]),
  );

  const unitTotals = new Map();
  for (const item of ledger.items.filter((candidate) => candidate.status !== 'cancelled')) {
    unitTotals.set(item.unit_id, (unitTotals.get(item.unit_id) || 0) + item.total_cents);
  }
  line('單位統計', { size: 13 });
  table(
    [{ label: '單位', width: 160 }, { label: 'HKD', width: 120 }, { label: '港仙', width: 120 }],
    [...unitTotals.entries()].map(([unitId, cents]) => [
      unitLabel(ledger, unitId), (cents / 100).toFixed(2), String(cents),
    ]),
  );

  line('付款紀錄（證明參考可在已授權 App 內開啟付款證明）', { size: 13 });
  table(
    [
      { label: '證明參考', width: 100 },
      { label: '成員', width: 70 },
      { label: '金額 HKD', width: 80 },
      { label: '狀態', width: 110 },
      { label: '日期', width: 90 },
      { label: '帳務月份', width: 70 },
    ],
    ledger.payments.map((payment) => [
      payment.reference,
      memberName(ledger, payment.member_id),
      (payment.amount_cents / 100).toFixed(2),
      payment.state_label,
      payment.date || '—',
      payment.accounting_month,
    ]),
  );

  line('報表只包含證明參考，不嵌入、不打包付款證明圖片，亦不列出圖片原始檔名。', { size: 9, colour: rgb(0.45, 0.43, 0.4) });

  return document.save();
}

export { downloadBlob, dateLabel, timestampLabel };
